import React, { useCallback, useEffect, useState } from "react";
import { Wallet, IndianRupee, ArrowDownToLine, Clock, QrCode, Pencil, Upload, Eye, Trash2, CheckCircle2, X, Building2, CheckCircle, XCircle, Loader } from "lucide-react";
import { toast } from "sonner";
import { api, API, getToken, formatApiError } from "@/lib/api";
import { inr, fmtDate, fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useApp } from "@/context/AppContext";

const UPI_RE = /^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z]{2,64}$/;
const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/;
export const fileUrl = (path) => `${API}/payments/file/${path}?token=${getToken() || ""}`;
export const statusColor = (s) => ({
  Paid: "text-emerald-500 bg-emerald-500/10", "Pending Approval": "text-amber-500 bg-amber-500/10",
  Pending: "text-amber-500 bg-amber-500/10", Approved: "text-blue-500 bg-blue-500/10",
  "Payment Processing": "text-cyan-500 bg-cyan-500/10", Processing: "text-cyan-500 bg-cyan-500/10",
  Rejected: "text-rose-500 bg-rose-500/10", Failed: "text-rose-500 bg-rose-500/10", Cancelled: "text-muted-foreground bg-secondary",
}[s] || "text-muted-foreground bg-secondary");
export const StatusBadgePill = ({ s }) => <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap ${statusColor(s)}`}>{s}</span>;

export function PaymentWithdrawal({ dpId }) {
  const { role, can } = useApp();
  const canManage = can("pay.create") || can("pay.edit_upi") || can("pay.upload_qr") || can("pay.approve");
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [payRec, setPayRec] = useState(null);
  const [rejectRec, setRejectRec] = useState(null);
  const [detailRec, setDetailRec] = useState(null);
  const [qrView, setQrView] = useState(false);
  const [acting, setActing] = useState("");

  const load = useCallback(() => {
    api.get(`/dp/${dpId}/payment-summary`).then(({ data }) => setSummary(data)).catch(() => {});
    api.get(`/dp/${dpId}/payments`).then(({ data }) => setHistory(data)).catch(() => {});
  }, [dpId]);
  useEffect(() => { load(); }, [load]);

  const act = async (pid, action, label) => {
    setActing(pid + action);
    try { await api.post(`/payments/${pid}/${action}`); toast.success(label); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); } finally { setActing(""); }
  };

  if (!summary) return <div className="animate-pulse text-muted-foreground">Loading payment data…</div>;
  const upi = summary.upi || {};
  const bank = summary.bank || {};

  return (
    <div className="space-y-5" data-testid="payment-withdrawal-section">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <SummaryCard icon={Wallet} label="Available Balance" value={inr(summary.available_balance)} accent="primary" tid="pay-available" />
        <SummaryCard icon={IndianRupee} label="Total Earnings" value={inr(summary.total_earnings)} accent="emerald" tid="pay-earnings" />
        <SummaryCard icon={ArrowDownToLine} label="Total Withdrawn" value={inr(summary.total_withdrawn)} accent="cyan" tid="pay-withdrawn" />
        <SummaryCard icon={Clock} label="Pending Withdrawal" value={inr(summary.pending_withdrawal)} accent="amber" tid="pay-pending" />
      </div>

      <div className="rounded-xl border border-border bg-card p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-head text-sm font-semibold">Payment Details</h3>
            <p className="text-xs text-muted-foreground">Last payment: {summary.last_payment_date ? `${inr(summary.last_payment_amount)} on ${fmtDate(summary.last_payment_date)} (${summary.last_payment_status})` : "—"}</p>
          </div>
          {canManage && <Button onClick={() => setWithdrawOpen(true)} data-testid="withdraw-payment-btn" className="active:scale-95"><ArrowDownToLine className="mr-1.5 h-4 w-4" /> Withdraw Payment</Button>}
        </div>

        <Tabs defaultValue="upi">
          <TabsList className="bg-secondary/50">
            <TabsTrigger value="upi" data-testid="pay-tab-upi">UPI ID</TabsTrigger>
            <TabsTrigger value="qr" data-testid="pay-tab-qr">UPI QR Code</TabsTrigger>
            <TabsTrigger value="bank" data-testid="pay-tab-bank"><Building2 className="mr-1.5 h-3.5 w-3.5" /> Bank Details</TabsTrigger>
          </TabsList>
          <TabsContent value="upi" className="mt-3"><UpiPanel dpId={dpId} upi={upi} canManage={canManage} onSaved={load} /></TabsContent>
          <TabsContent value="qr" className="mt-3"><QrPanel dpId={dpId} upi={upi} canManage={canManage} onSaved={load} onView={() => setQrView(true)} /></TabsContent>
          <TabsContent value="bank" className="mt-3"><BankPanel dpId={dpId} bank={bank} canManage={canManage} onSaved={load} /></TabsContent>
        </Tabs>
      </div>

      <div>
        <h3 className="mb-2 font-head text-sm font-semibold">Withdrawal History</h3>
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <Table>
            <TableHeader><TableRow className="hover:bg-transparent">
              <TableHead>Date</TableHead><TableHead>Withdrawal ID</TableHead><TableHead className="text-right">Amount</TableHead>
              <TableHead>Type</TableHead><TableHead>Status</TableHead><TableHead>UTR/Txn</TableHead><TableHead>Approved By</TableHead><TableHead className="text-right">Action</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {history.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground">No withdrawals yet</TableCell></TableRow>}
              {history.map((p) => (
                <TableRow key={p.payment_id} data-testid={`payment-row-${p.payment_id}`}>
                  <TableCell className="whitespace-nowrap">{fmtDate(p.payment_date || p.created_at)}</TableCell>
                  <TableCell className="font-mono text-xs">{p.payment_id}</TableCell>
                  <TableCell className="text-right tabular font-semibold">{inr(p.withdrawal_amount)}</TableCell>
                  <TableCell className="text-xs">{p.payment_type || p.payment_method}</TableCell>
                  <TableCell>
                    <StatusBadgePill s={p.status} />
                    {p.status === "Rejected" && p.rejection_reason && <p className="mt-0.5 text-[10px] text-rose-400">{p.rejection_reason}</p>}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{p.utr_number || "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{p.approved_by || "—"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex flex-wrap items-center justify-end gap-1">
                      <Button size="sm" variant="ghost" className="h-8 px-2 text-xs" data-testid={`view-payment-${p.payment_id}`} onClick={() => setDetailRec(p)}>View</Button>
                      {canManage && p.status === "Pending Approval" && <>
                        <Button size="sm" variant="ghost" className="h-8 px-2 text-xs text-emerald-500" disabled={acting} data-testid={`approve-${p.payment_id}`} onClick={() => act(p.payment_id, "approve", "Approved")}><CheckCircle className="mr-1 h-3.5 w-3.5" />Approve</Button>
                        <Button size="sm" variant="ghost" className="h-8 px-2 text-xs text-rose-500" data-testid={`reject-${p.payment_id}`} onClick={() => setRejectRec(p)}><XCircle className="mr-1 h-3.5 w-3.5" />Reject</Button>
                      </>}
                      {canManage && p.status === "Approved" &&
                        <Button size="sm" variant="ghost" className="h-8 px-2 text-xs text-cyan-500" disabled={acting} data-testid={`process-${p.payment_id}`} onClick={() => act(p.payment_id, "process", "Processing")}><Loader className="mr-1 h-3.5 w-3.5" />Process</Button>}
                      {canManage && (p.status === "Approved" || p.status === "Payment Processing") &&
                        <Button size="sm" variant="ghost" className="h-8 px-2 text-xs text-emerald-500" data-testid={`mark-paid-${p.payment_id}`} onClick={() => setPayRec(p)}>Mark Paid</Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <WithdrawModal open={withdrawOpen} onClose={() => setWithdrawOpen(false)} dpId={dpId} summary={summary} onSaved={load} />
      <MarkPaidModal rec={payRec} onClose={() => setPayRec(null)} onSaved={load} />
      <RejectModal rec={rejectRec} onClose={() => setRejectRec(null)} onSaved={load} />
      <DetailModal rec={detailRec} onClose={() => setDetailRec(null)} />
      <QrViewer open={qrView} onClose={() => setQrView(false)} upi={upi} dp={summary.dp} />
    </div>
  );
}

const SummaryCard = ({ icon: Icon, label, value, accent, tid }) => (
  <div className="rounded-xl border border-border bg-card p-4" data-testid={tid}>
    <div className="flex items-center justify-between">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <Icon className={`h-4 w-4 ${accent === "emerald" ? "text-emerald-500" : accent === "amber" ? "text-amber-500" : accent === "cyan" ? "text-cyan-500" : "text-primary"}`} />
    </div>
    <p className="mt-2 font-head text-2xl font-bold tabular">{value}</p>
  </div>
);

function UpiPanel({ dpId, upi, canManage, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState(upi.upi_id || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setVal(upi.upi_id || ""); setEdit(false); }, [upi.upi_id]);
  const save = async () => {
    if (!UPI_RE.test(val.trim())) { toast.error("Invalid UPI ID. Example: name@upi, 9876543210@ybl"); return; }
    setSaving(true);
    try { await api.post(`/dp/${dpId}/upi`, { upi_id: val.trim() }); toast.success("UPI ID saved"); setEdit(false); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };
  if (!upi.upi_id || edit) {
    return (
      <div className="rounded-lg border border-border bg-secondary/30 p-4">
        <Label>UPI ID</Label>
        <div className="mt-1 flex gap-2">
          <Input value={val} onChange={(e) => setVal(e.target.value)} placeholder="example@upi" data-testid="upi-input" disabled={!canManage} />
          <Button onClick={save} disabled={saving || !canManage} data-testid="save-upi-btn">{upi.upi_id ? "Update" : "Save"} UPI ID</Button>
          {edit && <Button variant="outline" onClick={() => setEdit(false)}>Cancel</Button>}
        </div>
        <p className="mt-2 text-xs text-muted-foreground">Format only — not bank-verified. Examples: name@upi · 9876543210@ybl · example@okaxis</p>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-border bg-secondary/30 p-4">
      <div className="flex items-center justify-between">
        <div><p className="text-[11px] uppercase tracking-wider text-muted-foreground">UPI ID</p><p className="font-head text-lg font-bold" data-testid="upi-value">{upi.upi_id}</p></div>
        {canManage && <Button variant="outline" size="sm" onClick={() => setEdit(true)} data-testid="edit-upi-btn"><Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit UPI ID</Button>}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-muted-foreground sm:grid-cols-3">
        <span>Added: {upi.upi_added_at ? fmtDate(upi.upi_added_at) : "—"}</span>
        <span>Last updated: {upi.upi_updated_at ? fmtDate(upi.upi_updated_at) : "—"}</span>
        <span>By: {upi.upi_updated_by || upi.upi_added_by || "—"}</span>
      </div>
      <p className="mt-2 text-[11px] text-muted-foreground">UPI ID is stored as-is and is not bank-verified.</p>
    </div>
  );
}

function BankPanel({ dpId, bank, canManage, onSaved }) {
  const blank = { account_holder: "", bank_name: "", account_number: "", ifsc: "", account_type: "Savings" };
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  useEffect(() => { setF({ ...blank, ...bank }); setEdit(false); }, [bank]);

  const save = async () => {
    if (!f.account_holder || !f.bank_name || !f.account_number || !f.ifsc) { toast.error("Fill all required bank fields"); return; }
    if (!/^\d{8,18}$/.test(f.account_number)) { toast.error("Account number must be 8-18 digits"); return; }
    if (!IFSC_RE.test(f.ifsc.toUpperCase())) { toast.error("Invalid IFSC. Example: SBIN0123456"); return; }
    setSaving(true);
    try { await api.post(`/dp/${dpId}/bank`, f); toast.success("Bank details saved"); setEdit(false); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };
  const uploadCheque = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) { toast.error("Only JPG/PNG/WEBP"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5 MB"); return; }
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/dp/${dpId}/bank/cheque`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Bank proof uploaded"); onSaved(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); } finally { setUploading(false); }
  };

  if (!bank.has_bank || edit) {
    return (
      <div className="rounded-lg border border-border bg-secondary/30 p-4" data-testid="bank-form">
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Account Holder Name *</Label><Input value={f.account_holder} onChange={(e) => set("account_holder", e.target.value)} data-testid="bank-holder" disabled={!canManage} /></div>
          <div><Label>Bank Name *</Label><Input value={f.bank_name} onChange={(e) => set("bank_name", e.target.value)} data-testid="bank-name" disabled={!canManage} /></div>
          <div><Label>Account Number *</Label><Input value={f.account_number} onChange={(e) => set("account_number", e.target.value)} data-testid="bank-acct" disabled={!canManage} /></div>
          <div><Label>IFSC Code *</Label><Input value={f.ifsc} onChange={(e) => set("ifsc", e.target.value.toUpperCase())} placeholder="SBIN0123456" data-testid="bank-ifsc" disabled={!canManage} /></div>
          <div><Label>Account Type</Label>
            <Select value={f.account_type} onValueChange={(v) => set("account_type", v)}>
              <SelectTrigger data-testid="bank-type"><SelectValue /></SelectTrigger>
              <SelectContent>{["Savings", "Current"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Cancelled Cheque / Bank Proof (optional)</Label>
            <input type="file" accept=".jpg,.jpeg,.png,.webp" onChange={uploadCheque} disabled={!canManage} className="mt-1 block w-full text-xs text-muted-foreground file:mr-2 file:rounded-lg file:border-0 file:bg-secondary file:px-3 file:py-1.5 file:text-xs" data-testid="bank-cheque" /></div>
        </div>
        <div className="mt-3 flex gap-2">
          {canManage && <Button onClick={save} disabled={saving} data-testid="save-bank-btn">{saving ? "Saving…" : "Save Bank Details"}</Button>}
          {edit && <Button variant="outline" onClick={() => setEdit(false)}>Cancel</Button>}
        </div>
        {bank.cheque_file && <a href={fileUrl(bank.cheque_file)} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-primary hover:underline"><Eye className="h-3 w-3" /> View uploaded proof</a>}
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-border bg-secondary/30 p-4" data-testid="bank-view">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2"><Building2 className="h-5 w-5 text-primary" /><p className="font-head text-lg font-bold">{bank.bank_name}</p></div>
        {canManage && <Button variant="outline" size="sm" onClick={() => setEdit(true)} data-testid="edit-bank-btn"><Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit</Button>}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-sm sm:grid-cols-3">
        <div><p className="text-[11px] uppercase tracking-wider text-muted-foreground">Account Holder</p><p className="font-medium">{bank.account_holder}</p></div>
        <div><p className="text-[11px] uppercase tracking-wider text-muted-foreground">Account Number</p><p className="font-mono font-medium" data-testid="bank-acct-view">{bank.account_number}</p></div>
        <div><p className="text-[11px] uppercase tracking-wider text-muted-foreground">IFSC</p><p className="font-mono font-medium">{bank.ifsc}</p></div>
        <div><p className="text-[11px] uppercase tracking-wider text-muted-foreground">Account Type</p><p className="font-medium">{bank.account_type}</p></div>
        <div><p className="text-[11px] uppercase tracking-wider text-muted-foreground">Last Updated</p><p className="text-xs text-muted-foreground">{bank.bank_updated_at ? fmtDate(bank.bank_updated_at) : "—"}</p></div>
      </div>
      {bank.cheque_file && <a href={fileUrl(bank.cheque_file)} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-primary hover:underline" data-testid="view-cheque"><Eye className="h-3 w-3" /> View Bank Proof</a>}
    </div>
  );
}

function QrPanel({ dpId, upi, canManage, onSaved, onView }) {
  const [uploading, setUploading] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const upload = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) { toast.error("Only JPG, PNG or WEBP allowed"); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("File exceeds 5 MB"); return; }
    setUploading(true);
    const fd = new FormData(); fd.append("file", f);
    try { await api.post(`/dp/${dpId}/qr`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("QR uploaded"); onSaved(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); } finally { setUploading(false); }
  };
  const del = async () => {
    try { await api.delete(`/dp/${dpId}/qr`); toast.success("QR deleted"); setDelOpen(false); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  if (!upi.qr_file) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-secondary/30 p-6 text-center">
        <QrCode className="mx-auto h-10 w-10 text-muted-foreground/50" />
        <p className="mt-2 text-sm text-muted-foreground">No UPI QR uploaded yet</p>
        {canManage && <label className="mt-3 inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90" data-testid="upload-qr-btn">
          <Upload className="h-4 w-4" /> {uploading ? "Uploading…" : "Upload UPI QR"}
          <input type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" onChange={upload} data-testid="qr-file-input" />
        </label>}
        <p className="mt-2 text-[11px] text-muted-foreground">JPG / JPEG / PNG / WEBP · max 5 MB</p>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-border bg-secondary/30 p-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <img src={fileUrl(upi.qr_file)} alt="UPI QR" className="h-32 w-32 rounded-lg border border-border bg-white object-contain" data-testid="qr-thumb" />
        <div className="flex-1">
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={onView} data-testid="view-qr-btn"><Eye className="mr-1.5 h-3.5 w-3.5" /> View QR</Button>
            {canManage && <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm font-medium hover:bg-accent" data-testid="replace-qr-btn">
              <Upload className="h-3.5 w-3.5" /> Replace QR<input type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" onChange={upload} /></label>}
            {canManage && <Button size="sm" variant="outline" className="text-rose-500" onClick={() => setDelOpen(true)} data-testid="delete-qr-btn"><Trash2 className="mr-1.5 h-3.5 w-3.5" /> Delete QR</Button>}
          </div>
          <div className="mt-3 grid grid-cols-1 gap-1 text-xs text-muted-foreground sm:grid-cols-2">
            <span>Uploaded: {upi.qr_uploaded_at ? fmtDate(upi.qr_uploaded_at) : "—"}</span>
            <span>By: {upi.qr_uploaded_by || "—"}</span>
            <span>Last updated: {upi.qr_updated_at ? fmtDate(upi.qr_updated_at) : "—"}</span>
          </div>
        </div>
      </div>
      <AlertDialog open={delOpen} onOpenChange={setDelOpen}>
        <AlertDialogContent data-testid="delete-qr-dialog">
          <AlertDialogHeader><AlertDialogTitle>Delete UPI QR?</AlertDialogTitle>
            <AlertDialogDescription>This removes the stored QR image for this partner.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-qr">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function WithdrawModal({ open, onClose, dpId, summary, onSaved }) {
  const [amount, setAmount] = useState("");
  const [type, setType] = useState("UPI");
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);
  const upi = summary.upi || {};
  const bank = summary.bank || {};
  useEffect(() => { if (open) { setAmount(""); setType("UPI"); setRemarks(""); } }, [open]);

  const submit = async () => {
    const amt = Number(amount || 0);
    if (amt <= 0) { toast.error("Amount must be greater than ₹0"); return; }
    if (amt > summary.available_balance) { toast.error(`Cannot exceed available balance (${inr(summary.available_balance)})`); return; }
    if (type === "UPI" && !upi.upi_id && !upi.qr_file) { toast.error("Add a UPI ID or QR first"); return; }
    if (type === "Bank Transfer" && !bank.has_bank) { toast.error("Add bank details first"); return; }
    setSaving(true);
    try {
      await api.post("/payments", { delivery_partner_id: dpId, withdrawal_amount: amt, payment_method: type, remarks });
      toast.success("Withdrawal request created", { description: `${inr(amt)} via ${type} · Pending Approval` });
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card" data-testid="withdraw-modal">
        <DialogHeader><DialogTitle className="font-head">Withdraw Payment</DialogTitle></DialogHeader>
        <div className="rounded-lg bg-primary/5 px-3 py-2 text-sm">Available Balance: <span className="font-bold text-primary">{inr(summary.available_balance)}</span></div>
        <div className="space-y-3">
          <div><Label>Withdrawal Amount</Label>
            <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Enter amount" max={summary.available_balance} data-testid="withdraw-amount-input" /></div>
          <div>
            <Label>Payment Type</Label>
            <div className="mt-1 grid grid-cols-2 gap-2">
              {["UPI", "Bank Transfer"].map((val) => (
                <button key={val} onClick={() => setType(val)} data-testid={`ptype-${val === "UPI" ? "upi" : "bank"}`}
                  className={`flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium ${type === val ? "border-primary bg-primary/10 text-primary" : "border-border"}`}>
                  {val === "Bank Transfer" ? <Building2 className="h-4 w-4" /> : <Wallet className="h-4 w-4" />}{val}</button>
              ))}
            </div>
          </div>
          {type === "UPI" && (
            <div className="rounded-lg border border-border bg-secondary/30 p-3 text-xs" data-testid="withdraw-upi-preview">
              {upi.upi_id ? <p>UPI ID: <span className="font-mono font-medium">{upi.upi_id}</span></p> : null}
              {upi.qr_file ? <p className="mt-1 text-muted-foreground">UPI QR available — team scans it to pay.</p> : null}
              {!upi.upi_id && !upi.qr_file && <p className="text-rose-400">No UPI ID or QR saved — add one in the UPI tabs.</p>}
            </div>
          )}
          {type === "Bank Transfer" && (
            <div className="rounded-lg border border-border bg-secondary/30 p-3 text-xs" data-testid="withdraw-bank-preview">
              {bank.has_bank ? (
                <div className="space-y-0.5">
                  <p className="font-semibold">{bank.bank_name}</p>
                  <p>{bank.account_holder}</p>
                  <p className="font-mono">A/C {bank.account_number} · IFSC {bank.ifsc}</p>
                  <p className="text-muted-foreground">{bank.account_type}</p>
                </div>
              ) : <p className="text-rose-400">No bank details saved — add them in the Bank Details tab.</p>}
            </div>
          )}
          <div><Label>Remarks (optional)</Label><Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="submit-withdraw">{saving ? "Creating…" : "Confirm Withdrawal"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TdsSummary({ d, rec }) {
  const t = d.tds, c = d.context || {};
  const rows = [["DP Name", rec.dp_name], ["FE ID", rec.fe_id], ["Gross Earnings (all-time)", inr(c.gross_earnings)],
    ["Total Penalties (all-time)", `− ${inr(c.total_penalties)}`], ["Other Adjustments (all-time)", inr(c.other_adjustments)],
    ["Gross Payable (this withdrawal)", inr(t.gross)], ["Applicable TDS Rate", t.applied ? `${t.rate}% (Sec ${t.section})` : "Not applied"],
    ...(t.catch_up ? [["Earlier untaxed amount (FY catch-up)", inr(t.catch_up)]] : []),
    ["TDS Amount", `− ${inr(t.tds_amount)}`]];
  return (
    <div className="rounded-lg border border-border bg-secondary/40 p-3 text-sm" data-testid="dp-payment-summary">
      <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Delivery Partner Payment Summary</p>
      {rows.map(([k, v]) => <div key={k} className="flex justify-between"><span className="text-muted-foreground">{k}</span><span className="tabular">{v}</span></div>)}
      <div className="mt-1 flex justify-between border-t border-border pt-1 font-semibold"><span>Final Net Payable</span><span className="tabular text-emerald-500" data-testid="dp-net-payable">{inr(t.net)}</span></div>
      {!t.applied && t.reason && <p className="mt-1 text-[11px] text-muted-foreground">{t.reason}</p>}
    </div>
  );
}

export function MarkPaidModal({ rec, onClose, onSaved }) {
  const [utr, setUtr] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [remarks, setRemarks] = useState("");
  const [file, setFile] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tds, setTds] = useState(null);
  useEffect(() => { if (rec) { setUtr(""); setDate(new Date().toISOString().slice(0, 10)); setRemarks(""); setFile(null); setConfirm(false); setTds(null);
    api.get(`/payments/${rec.payment_id}/tds-preview`).then(({ data }) => setTds(data)).catch(() => {}); } }, [rec]);
  const net = tds?.tds?.net ?? rec?.withdrawal_amount;

  const doSubmit = async () => {
    if (!utr.trim()) { toast.error("UTR / Transaction ID is required"); return; }
    setSaving(true);
    const fd = new FormData();
    fd.append("utr_number", utr.trim());
    fd.append("payment_date", new Date(date).toISOString());
    fd.append("remarks", remarks);
    if (file) fd.append("file", file);
    try {
      await api.post(`/payments/${rec.payment_id}/pay`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Payment marked as Paid");
      onSaved(); onClose();
    } catch (e) { if (e?.message !== "cancelled") toast.error(formatApiError(e?.response?.data?.detail)); } finally { setSaving(false); setConfirm(false); }
  };

  return (
    <Dialog open={!!rec} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card" data-testid="mark-paid-modal">
        {rec && <>
          <DialogHeader><DialogTitle className="font-head">Mark Payment as Paid</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{rec.dp_name} · {rec.payment_id} · {rec.payment_type || rec.payment_method}</p>
          <div className="space-y-3">
            {tds && <TdsSummary d={tds} rec={rec} />}
            <div><Label>Net Amount to Pay</Label><Input value={inr(net)} disabled data-testid="paid-amount" /></div>
            <div><Label>Payment Type</Label><Input value={rec.payment_type || rec.payment_method} disabled data-testid="paid-type" /></div>
            <div><Label>UTR / Transaction ID *</Label><Input value={utr} onChange={(e) => setUtr(e.target.value)} placeholder="e.g. NEFT123456 / 8293748293" data-testid="paid-utr" /></div>
            <div><Label>Payment Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="paid-date" /></div>
            <div><Label>Payment Proof (optional)</Label>
              <input type="file" accept=".jpg,.jpeg,.png,.webp" onChange={(e) => setFile(e.target.files?.[0] || null)} className="block w-full text-sm text-muted-foreground file:mr-3 file:rounded-lg file:border-0 file:bg-secondary file:px-3 file:py-1.5 file:text-sm" data-testid="paid-screenshot" /></div>
            <div><Label>Payment Remarks (optional)</Label><Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={() => { if (!utr.trim()) { toast.error("UTR / Transaction ID is required"); return; } setConfirm(true); }} data-testid="confirm-payment-btn" className="bg-emerald-600 hover:bg-emerald-700"><CheckCircle2 className="mr-1.5 h-4 w-4" /> Approve & Pay {inr(net)}</Button>
          </DialogFooter>
          <AlertDialog open={confirm} onOpenChange={setConfirm}>
            <AlertDialogContent data-testid="confirm-payment-dialog">
              <AlertDialogHeader><AlertDialogTitle>Payment Confirmation</AlertDialogTitle>
                <AlertDialogDescription>Are you sure you have manually completed this payment of {inr(net)} (net after TDS) to {rec.dp_name} via {rec.payment_type || rec.payment_method}? RazorpayX is not connected, so send this net amount by UPI/QR/bank yourself. This will permanently record it in payment history and the TDS ledger.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={doSubmit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="final-confirm-payment">{saving ? "Saving…" : "Confirm Payment"}</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>}
      </DialogContent>
    </Dialog>
  );
}

export function RejectModal({ rec, onClose, onSaved }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (rec) setReason(""); }, [rec]);
  const submit = async () => {
    if (!reason.trim()) { toast.error("A rejection reason is required"); return; }
    setSaving(true);
    try {
      await api.post(`/payments/${rec.payment_id}/reject`, { reason: reason.trim() });
      toast.success("Withdrawal rejected", { description: "Amount released back to available balance" });
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!rec} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card" data-testid="reject-modal">
        {rec && <>
          <DialogHeader><DialogTitle className="font-head text-rose-500">Reject Withdrawal</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{rec.dp_name} · {inr(rec.withdrawal_amount)} · {rec.payment_id}</p>
          <div className="rounded-lg bg-emerald-500/5 px-3 py-2 text-xs text-muted-foreground">The amount will be returned to the partner's Available Balance.</div>
          <div><Label>Rejection Reason *</Label><Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Incorrect bank details" data-testid="reject-reason" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={submit} disabled={saving || !reason.trim()} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-reject">{saving ? "Rejecting…" : "Reject Withdrawal"}</Button>
          </DialogFooter>
        </>}
      </DialogContent>
    </Dialog>
  );
}

export function DetailModal({ rec, onClose }) {
  return (
    <Dialog open={!!rec} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card" data-testid="payment-detail-modal">
        {rec && <>
          <DialogHeader><DialogTitle className="font-head">Payment Details</DialogTitle></DialogHeader>
          <div className="space-y-1.5 text-sm">
            <Row k="Withdrawal ID" v={rec.payment_id} mono />
            <Row k="Delivery Partner" v={rec.dp_name} />
            <Row k="FE ID" v={rec.fe_id} mono />
            <Row k="Location" v={rec.location || "—"} />
            <Row k="Franchise Partner" v={rec.franchise_partner_name || "—"} />
            <Row k="Amount" v={inr(rec.withdrawal_amount)} strong />
            <Row k="Payment Type" v={rec.payment_type || rec.payment_method} />
            {rec.upi_id && <Row k="UPI ID" v={rec.upi_id} mono />}
            {rec.bank && rec.bank.has_bank && <>
              <Row k="Bank" v={rec.bank.bank_name} />
              <Row k="Account" v={rec.bank.account_number} mono />
              <Row k="IFSC" v={rec.bank.ifsc} mono />
            </>}
            <Row k="UTR / Txn ID" v={rec.utr_number || "—"} mono />
            <Row k="Request Date" v={fmtDateTime(rec.created_at)} />
            <Row k="Approved By" v={rec.approved_by ? `${rec.approved_by}${rec.approved_at ? " · " + fmtDateTime(rec.approved_at) : ""}` : "—"} />
            {rec.status === "Rejected" && <>
              <Row k="Rejected By" v={rec.rejected_by || "—"} />
              <Row k="Rejection Reason" v={<span className="text-rose-400">{rec.rejection_reason}</span>} />
            </>}
            <Row k="Payment Date" v={rec.payment_date ? fmtDateTime(rec.payment_date) : "—"} />
            <Row k="Status" v={<StatusBadgePill s={rec.status} />} />
            <Row k="Paid By" v={rec.paid_by || "—"} />
            <Row k="Created By" v={rec.created_by || "—"} />
            <Row k="Remarks" v={rec.remarks || "—"} />
          </div>
          {rec.payment_screenshot && (
            <a href={fileUrl(rec.payment_screenshot)} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-sm text-primary hover:underline" data-testid="view-screenshot">
              <Eye className="h-4 w-4" /> View Payment Proof
            </a>
          )}
          <DialogFooter><Button onClick={onClose}>Close</Button></DialogFooter>
        </>}
      </DialogContent>
    </Dialog>
  );
}
const Row = ({ k, v, mono, strong }) => (
  <div className="flex items-center justify-between gap-4 border-b border-border/50 py-1"><span className="text-muted-foreground">{k}</span><span className={`text-right ${mono ? "font-mono text-xs" : ""} ${strong ? "font-bold text-primary" : "font-medium"}`}>{v}</span></div>
);

function QrViewer({ open, onClose, upi, dp }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-card text-center" data-testid="qr-viewer-modal">
        <DialogHeader><DialogTitle className="font-head text-center">Scan to Pay</DialogTitle></DialogHeader>
        <p className="text-sm font-semibold">{dp?.full_name}</p>
        <p className="font-mono text-xs text-muted-foreground">{dp?.fe_id}</p>
        {upi?.qr_file && <img src={fileUrl(upi.qr_file)} alt="UPI QR" className="mx-auto my-3 h-72 w-72 rounded-xl border border-border bg-white object-contain" data-testid="qr-large" />}
        <p className="text-xs text-muted-foreground">Scan this QR from another device to make the payment manually. No payment is initiated by the system.</p>
        <DialogFooter className="sm:justify-center"><Button onClick={onClose}><X className="mr-1.5 h-4 w-4" /> Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
